import React from "react";
import { Phone } from "lucide-react";
import { restaurantInfo } from "../data/mock";
import { RESTAURANT_INTERIOR, GARDEN } from "../data/assets";
import Reveal from "../components/Reveal";
import { useI18n } from "../data/i18n";

const DiamondDivider = () => (
  <div className="flex items-center justify-center gap-2 my-4">
    <div className="w-12 h-px bg-gradient-to-r from-transparent to-[#e4c590]"></div>
    <span className="diamond-mark"></span>
    <span className="diamond-mark opacity-60"></span>
    <span className="diamond-mark"></span>
    <div className="w-12 h-px bg-gradient-to-l from-transparent to-[#e4c590]"></div>
  </div>
);

const About = () => {
  const { t, lang } = useI18n();

  const story = lang === "bg"
    ? "Каним ви в лежерна средиземноморска атмосфера. Нашата локация на Симеоновско шосе 4 (в сградата на Hotel Hayashi) предлага автентичен дух в модерна и комфортна обстановка."
    : "We welcome you to a relaxed Mediterranean atmosphere. Our location at 4 Simeonovsko Shose (in the Hotel Hayashi building) brings authentic spirit into a modern, comfortable setting.";

  const teamBody = lang === "bg"
    ? "В кухнята на CASA BALA всеки ден е посветен на най-важното изкуство – да нахраним сърцата. Нашите готвачи превръщат пресните средиземноморски съставки в истории на вкуса."
    : "In the CASA BALA kitchen every day is devoted to a single art — feeding hearts. Our chefs turn fresh Mediterranean ingredients into stories of flavour.";

  const teamBody2 = lang === "bg"
    ? "С професионализъм и усмивка, екипът ни ще ви разкрие идеалната хармония между ястия и напитки."
    : "With professionalism and a smile, our team helps you discover the perfect harmony between dish and drink.";

  const goals = lang === "bg"
    ? "В CASA BALA ви предлагаме повече от вечеря – емоция, пренесена директно от средиземноморския бряг. Усещате топлината на южното слънце във всяка хапка."
    : "At CASA BALA we offer more than dinner — emotion brought straight from the Mediterranean shore. You'll taste the warmth of the southern sun in every bite.";

  const history = lang === "bg"
    ? "Историята ни е плод на споделена страст към средиземноморската култура. Започна около една маса – екип от мечтатели, вдъхновени от морето, билките и уюта на южните заведения."
    : "Our story grew from a shared love of Mediterranean culture. It began around a single table — a team of dreamers inspired by the sea, herbs and southern hospitality.";

  return (
    <div className="bg-[#0a0a0a] pt-32 pb-24 min-h-screen">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24">
        <Reveal direction="left">
          <div>
            <p className="section-label">{t("about.label")}</p>
            <h1 className="display text-4xl md:text-6xl text-[#f5f0e6] mt-3 mb-6 leading-tight">{t("about.title")}</h1>
            <DiamondDivider />
            <p className="text-[#cfc7b8] leading-relaxed mb-6">{story}</p>
            <p className="text-[#cfc7b8] mb-2">{t("story.call")}</p>
            <a href={`tel:${restaurantInfo.phone}`} className="text-[#e4c590] flex items-center gap-2 hover:opacity-80">
              <Phone size={18} />
              <span className="text-lg">{restaurantInfo.phone}</span>
            </a>
          </div>
        </Reveal>
        <Reveal direction="right" delay={150}>
          <div className="img-hover aspect-[4/5] overflow-hidden border-2 border-[#e4c590]/20 p-2">
            <img src={RESTAURANT_INTERIOR} alt={lang === "bg" ? "Интериорът на ресторант CASA BALA" : "CASA BALA restaurant interior"} className="w-full h-full object-cover" loading="lazy" decoding="async" />
          </div>
        </Reveal>
      </section>

      {/* Chefs */}
      <section className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24">
        <Reveal direction="left">
          <div className="order-2 lg:order-1 img-hover aspect-[4/5] overflow-hidden">
            <img src={GARDEN} alt={lang === "bg" ? "Градината на ресторант CASA BALA" : "CASA BALA restaurant garden"} className="w-full h-full object-cover" loading="lazy" decoding="async" />
          </div>
        </Reveal>
        <Reveal direction="right" delay={150}>
          <div className="order-1 lg:order-2">
            <p className="section-label">{t("about.team_label")}</p>
            <h2 className="display text-4xl md:text-5xl text-[#f5f0e6] mt-3 mb-6">{t("about.team_title")}</h2>
            <DiamondDivider />
            <p className="text-[#cfc7b8] leading-relaxed mb-4">{teamBody}</p>
            <p className="text-[#cfc7b8] leading-relaxed">{teamBody2}</p>
          </div>
        </Reveal>
      </section>

      {/* Goals */}
      <section className="max-w-4xl mx-auto px-6 text-center mb-24">
        <Reveal direction="up">
          <p className="section-label">{t("about.goals_label")}</p>
          <h2 className="display text-4xl md:text-5xl text-[#f5f0e6] mt-3 mb-6">{t("about.goals_title")}</h2>
          <DiamondDivider />
          <p className="text-[#cfc7b8] leading-relaxed">{goals}</p>
        </Reveal>
      </section>

      {/* History */}
      <section className="max-w-4xl mx-auto px-6 text-center">
        <Reveal direction="up">
          <p className="section-label">{t("about.history_label")}</p>
          <h2 className="display text-4xl md:text-5xl text-[#f5f0e6] mt-3 mb-6">{t("about.history_title")}</h2>
          <DiamondDivider />
          <p className="text-[#cfc7b8] leading-relaxed mb-6">{history}</p>
          <p className="text-[#e4c590] display text-2xl italic">
            {lang === "bg" ? "Добре дошли в CASA BALA." : "Welcome to CASA BALA."}
          </p>
        </Reveal>
      </section>
    </div>
  );
};

export default About;
